import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  Chip,
  Paper,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  AccountTree as GroupIcon,
  Lock as LockIcon,
  CheckCircle as UnlockedIcon,
  East as ArrowRightIcon,
  DragIndicator as DragIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { groupAndSequenceTasks } from "services/company/projectManagement/task/taskService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const GroupSequenceTasksModal = ({
  open,
  onClose,
  selectedTasks = [],
  projects = [],
  onSuccess,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [orderedTasks, setOrderedTasks] = useState([]);
  const [autoAlignDates, setAutoAlignDates] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Sync selectedTasks into orderedTasks whenever modal opens
  useEffect(() => {
    if (open && selectedTasks && selectedTasks.length > 0) {
      setOrderedTasks([...selectedTasks]);
      setTitle("");
      setDescription("");
      setAutoAlignDates(true);
    }
  }, [open, selectedTasks]);

  // Determine common project_id
  const targetProjectId =
    orderedTasks.length > 0 ? orderedTasks[0].project_id : null;
  const projectObj = projects.find((p) => p.id === targetProjectId);
  const projectName = projectObj
    ? projectObj.name
    : `Project #${targetProjectId || ""}`;

  // Check if selected tasks belong to multiple projects
  const hasMultipleProjects = orderedTasks.some(
    (t) => t.project_id !== targetProjectId,
  );

  const moveTask = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= orderedTasks.length) return;

    const updated = [...orderedTasks];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIndex, 0, moved);
    setOrderedTasks(updated);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      showErrorToast("Please enter a Mother Task (Group) title");
      return;
    }

    if (orderedTasks.length < 2) {
      showErrorToast("Please select at least 2 tasks to group and sequence");
      return;
    }

    if (!targetProjectId) {
      showErrorToast("Could not determine project for these tasks");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        project_id: targetProjectId,
        title: title.trim(),
        description: description.trim(),
        task_ids: orderedTasks.map((t) => t.id),
        auto_align_dates: autoAlignDates,
      };

      const response = await groupAndSequenceTasks(payload);

      if (response?.meta?.status === true || response?.meta?.code === 200) {
        showSuccessToast(
          response?.meta?.message ||
            `Mother Task "${title}" created and ${orderedTasks.length} tasks sequenced successfully!`,
        );
        if (onSuccess) onSuccess();
        onClose();
      } else {
        showErrorToast(
          response?.meta?.message || "Failed to group and sequence tasks",
        );
      }
    } catch (err) {
      console.error("Error grouping tasks:", err);
      showErrorToast(
        err?.response?.data?.meta?.message ||
          err?.message ||
          "Failed to group and sequence tasks",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "#ef4444";
      case "high":
        return "#f97316";
      case "medium":
        return "#eab308";
      case "low":
        return "#22c55e";
      default:
        return "#64748b";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!submitting ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: isDark
            ? "0 25px 50px -12px rgba(0,0,0,0.8)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Accent Header Bar */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
          height: 6,
        }}
      />

      {/* Dialog Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              background: isDark
                ? "rgba(245, 158, 11, 0.15)"
                : "rgba(245, 158, 11, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#f59e0b",
            }}
          >
            <GroupIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Group & Arrange Task Sequence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bundle selected tasks into a Mother Task and set the completion
              order
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={3}>
          {hasMultipleProjects && (
            <Alert severity="warning">
              Selected tasks belong to different projects. They will all be
              grouped under <strong>{projectName}</strong>.
            </Alert>
          )}

          {/* Group Details */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
              }`,
              background: isDark
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: "text.primary" }}
            >
              Mother Task Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Mother Task Title"
                placeholder="e.g. Sprint Release, Client Onboarding, Phase 1"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Description (Optional)"
                placeholder="Brief summary of this task group's objective..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Target Project:
                </Typography>
                <Chip
                  size="small"
                  label={projectName}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    bgcolor: isDark
                      ? "rgba(245, 158, 11, 0.15)"
                      : "rgba(245, 158, 11, 0.1)",
                    color: "#f59e0b",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  ({orderedTasks.length} tasks selected)
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Sequence Arrangement Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Completion Sequence (Which one to complete first)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Use the arrows (▲ ▼) to arrange tasks in order. Step 1 must
                  finish before Step 2 can be started.
                </Typography>
              </Box>
            </Box>

            {/* Visual Flow Preview */}
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: "10px",
                bgcolor: isDark
                  ? "rgba(245, 158, 11, 0.08)"
                  : "rgba(245, 158, 11, 0.05)",
                border: "1px dashed rgba(245, 158, 11, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                overflowX: "auto",
                pb: 1.5,
              }}
            >
              {orderedTasks.map((task, idx) => (
                <React.Fragment key={task.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: "8px",
                      bgcolor: isDark ? "#1e293b" : "#ffffff",
                      border: `1px solid ${
                        idx === 0
                          ? "#22c55e"
                          : isDark
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0,0,0,0.1)"
                      }`,
                      flexShrink: 0,
                      maxWidth: 200,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: idx === 0 ? "#22c55e" : "#f59e0b",
                        color: "#fff",
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      noWrap
                      sx={{ maxWidth: 140 }}
                    >
                      {task.title}
                    </Typography>
                  </Box>
                  {idx < orderedTasks.length - 1 && (
                    <ArrowRightIcon
                      sx={{ color: "#f59e0b", fontSize: 20, flexShrink: 0 }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>

            {/* Reorderable List of Tasks */}
            <Stack spacing={1.5}>
              {orderedTasks.map((task, index) => {
                const isFirst = index === 0;
                const isLast = index === orderedTasks.length - 1;
                const dependsOnTask = !isFirst ? orderedTasks[index - 1] : null;

                return (
                  <Paper
                    key={task.id}
                    elevation={0}
                    sx={{
                      p: 1.75,
                      borderRadius: "10px",
                      border: `1px solid ${
                        isFirst
                          ? "rgba(34, 197, 94, 0.4)"
                          : isDark
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)"
                      }`,
                      bgcolor: isFirst
                        ? isDark
                          ? "rgba(34, 197, 94, 0.05)"
                          : "rgba(34, 197, 94, 0.03)"
                        : isDark
                        ? "rgba(255,255,255,0.02)"
                        : "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: isDark
                          ? "0 4px 12px rgba(0,0,0,0.3)"
                          : "0 4px 12px rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      {/* Left: Step Badge + Info */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            bgcolor: isFirst ? "#22c55e" : "#f59e0b",
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: "0.85rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          #{index + 1}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              noWrap
                              sx={{ maxWidth: 380 }}
                            >
                              {task.title}
                            </Typography>
                            {task.priority && (
                              <Chip
                                size="small"
                                label={task.priority}
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  fontWeight: 600,
                                  bgcolor: `${getPriorityColor(
                                    task.priority,
                                  )}20`,
                                  color: getPriorityColor(task.priority),
                                }}
                              />
                            )}
                          </Stack>

                          {/* Dependency note */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            {isFirst ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#22c55e",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <UnlockedIcon sx={{ fontSize: 14 }} />
                                Starts 1st (No prerequisite)
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDark ? "#cbd5e1" : "#64748b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <LockIcon
                                  sx={{ fontSize: 13, color: "#f59e0b" }}
                                />
                                Locked until Step #{index} (
                                <strong style={{ color: "#f59e0b" }}>
                                  {dependsOnTask?.title}
                                </strong>
                                ) is Done
                              </Typography>
                            )}

                            {task.due_date && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                • Due: {task.due_date}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Right: Reorder Up / Down Controls */}
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Move earlier in sequence">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isFirst}
                              onClick={() => moveTask(index, "up")}
                              sx={{
                                border: `1px solid ${
                                  isDark
                                    ? "rgba(255,255,255,0.1)"
                                    : "rgba(0,0,0,0.1)"
                                }`,
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move later in sequence">
                          <span>
                            <IconButton
                              size="small"
                              disabled={isLast}
                              onClick={() => moveTask(index, "down")}
                              sx={{
                                border: `1px solid ${
                                  isDark
                                    ? "rgba(255,255,255,0.1)"
                                    : "rgba(0,0,0,0.1)"
                                }`,
                                "&:hover": {
                                  bgcolor: "action.hover",
                                },
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>

          <Divider />

          {/* Timeline auto-align toggle */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: "10px",
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
              }`,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoAlignDates}
                  onChange={(e) => setAutoAlignDates(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Auto-align task timelines sequentially (Waterfall)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automatically schedules Step 2 to start immediately after
                    Step 1 is due, preserving each task's duration.
                  </Typography>
                </Box>
              }
            />
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${
            isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
          }`,
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !title.trim() || orderedTasks.length < 2}
          startIcon={<GroupIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#ffffff",
            boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
            },
          }}
        >
          {submitting
            ? "Creating Mother Task..."
            : `Create Mother Task & Set Sequence (${orderedTasks.length} Tasks)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupSequenceTasksModal;
